// @ts-nocheck
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Calendar, Phone, Mail, Shield, FileText, ChevronDown, ChevronRight, Pencil, Settings, ClipboardList, Scan, Briefcase, X, Save, RefreshCw } from 'lucide-react';
import CyberSweepAuditDetail from './CyberSweepAuditDetail';
import { CreateCSAuditModal } from '../../components/cybersweep/CreateCSAuditModal';
import { cyberSweepClientService } from '../../services/cyberSweepClientService';
import DynamicTabRenderer from '../../components/dynamic/DynamicTabRenderer';
import { StandardButton } from '../../components/standard';
import {
  useCyberSweepClient,
  useCSAudit,
  useCSAudits,
  useAccountIdToName,
  useContactIdToName,
  useUserIdToName,
  buildCyberSweepClientLookupDisplayFields,
  lookupAccountDisplayName,
  useUpdateCyberSweepClient,
  useCreateCSAudit,
  useAuditTemplates,
} from '../../services/queries/cyberSweepQueries';
import { USE_GOBODO_API } from '../../utils/gobodoUtils';
import { readCyberSweepClientTraversalString } from '../../utils/cyberSweepClientBoardTraversalFields';
import { getCustomSuffix } from '../../config/gobodoConfig';
import { GobodoRequiredGuard } from '../../components/common/GobodoRequiredGuard';
import { SendEmailModal } from '../../components/modals/SendEmailModal';
import CSAuditsSettingsModal, { CSAuditsSettings, defaultCSAuditsSettings } from '../../components/cybersweep/CSAuditsSettingsModal';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { useUsers } from '../../services/queries/userQueries';
import { formatCyberSweepDisplayDate, formatCyberSweepDisplayDateTime } from '../../utils/cyberSweepDisplayDate';

interface CyberSweepClientDetailProps {
  clientId: string;
  auditId: string;
  onBack: () => void;
  onViewAudit?: (auditId: string) => void;
}

const fmt = (v: unknown, def = '') => (v != null && v !== '' ? String(v) : def);

const formatDate = formatCyberSweepDisplayDate;
const formatDateTime = formatCyberSweepDisplayDateTime;

// Field resolver: checks multiple naming conventions
const g = (obj: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};
const gAll = (r: Record<string, unknown>, data: Record<string, unknown>, ...keys: string[]): unknown =>
  g(r, ...keys) ?? g(data, ...keys);
/** Coerce Gobodo/SF checkbox-ish values for subscription flags. */
const asBool = (v: unknown): boolean => {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null || v === '') return false;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === 'yes' || s === '1';
};
const isGobodoLabel = (s: string) => /^(CyberAudit|CSAudit|PAWRecord|Client|PAW)-\d+$/i.test(s) || /^a\d[a-z]\d/i.test(s);

/** Avoid using short all-digit values as a display name (e.g. Name__c accidentally holds a year-like number "374" → date bugs in the form). */
const isTrivialNumericOnlyName = (s: string) => {
  const t = String(s ?? '').trim();
  if (!t) return true;
  return /^\d{1,3}$/.test(t);
};

/** Resolve last four digits for masked SSN line (API may send Last_4_SSN__c, SSN__c, or custom__*). */
function extractSsnLast4Digits(ssnFull: string, ssnLast4Field: string): string {
  const fromLast4 = String(ssnLast4Field || '').replace(/\D/g, '');
  if (fromLast4.length >= 4) return fromLast4.slice(-4);
  const fromFull = String(ssnFull || '').replace(/\D/g, '');
  if (fromFull.length >= 9) return fromFull.slice(-4);
  if (fromFull.length === 4) return fromFull;
  return '';
}

function formatSsnMaskedDisplay(ssnFull: string, ssnLast4Field: string): string {
  const last4 = extractSsnLast4Digits(ssnFull, ssnLast4Field);
  if (!last4) return '';
  return `***-**-${last4}`;
}

const mapToClientData = (r: Record<string, unknown> | null, clientId: string, auditId: string, nameMap?: Record<string, string>) => {
  if (!r) return null;
  const data = (r.data as Record<string, unknown>) ?? r;
  const suffix = getCustomSuffix();
  const rawName = fmt(gAll(r, data, 'clientName', 'ClientName__c', 'AccountName__c'));
  const sfName = fmt(gAll(r, data, 'Name__c', 'Name', 'name'));
  // First/last name fields directly on the record
  const recFirst = fmt(gAll(r, data, 'firstName', 'FirstName__c', 'First_Name__c', 'custom__first_name', 'first_name'));
  const recLast = fmt(gAll(r, data, 'lastName', 'LastName__c', 'Last_Name__c', 'custom__last_name', 'last_name'));
  const fullNameFromParts = [recFirst, recLast].filter(Boolean).join(' ').trim();
  const recordLabel = fmt(gAll(r, data, 'label'));
  // Lookup via account ID in the Client records map
  const accountRef = fmt(gAll(r, data, 'relatedClientId', 'clientId', 'ClientId__c', 'Account__c', 'custom__account'));
  const lookupName = lookupAccountDisplayName(nameMap, accountRef);
  const asDisplayName = (s: string) => s && !isGobodoLabel(s) && !isTrivialNumericOnlyName(s) ? s : '';
  const clientName = asDisplayName(rawName) ||
    asDisplayName(sfName) ||
    asDisplayName(fullNameFromParts) ||
    (recordLabel && !isGobodoLabel(recordLabel) && !recordLabel.startsWith('Record ') && !isTrivialNumericOnlyName(recordLabel)
      ? recordLabel
      : '') ||
    asDisplayName(lookupName);
  const parts = clientName.split(' ');

  const travAuditKey = readCyberSweepClientTraversalString(r, data, 'auditKey', suffix);
  return {
    id: fmt(r.id ?? data.id, clientId),
    auditId: fmt(travAuditKey || gAll(r, data, 'auditId', 'csAuditId', 'CS_Audit__c', 'CSAuditRelField__c'), auditId),
    firstName: fmt(gAll(r, data, 'firstName', 'FirstName__c', 'custom__first_name') ?? parts[0]),
    lastName: fmt(gAll(r, data, 'lastName', 'LastName__c', 'custom__last_name') ?? parts.slice(1).join(' ')),
    clientName,
    cyberSweepStartDate: fmt(gAll(r, data, 'cyberSweepStartDate', 'CyberSweep_Start_Date__c', 'CyberSweepStartDate__c', 'custom__cyber_sweep_start_date')),
    cyberSweepEndDate: fmt(gAll(r, data, 'cyberSweepEndDate', 'CyberSweep_End_Date__c', 'CyberSweepEndDate__c', 'custom__cyber_sweep_end_date')),
    cyberSweepStage: fmt(gAll(r, data, 'cyberSweepStage', 'CyberSweep_Stage__c', 'CyberSweepStage__c', 'custom__cyber_sweep_stage')),
    cyberSweepStatus: fmt(gAll(r, data, 'cyberSweepStatus', 'Cyber_Sweep_Status__c', 'CyberSweepStatus__c', 'custom__cyber_sweep_status', 'Custom__cyber_sweep_status__c', `Cyber_Sweep_Status${suffix}`)),
    phaseStatus: fmt(
      readCyberSweepClientTraversalString(r, data, 'phaseStatus', suffix) ||
        gAll(r, data, 'phaseStatus', 'Phase_Status__c', 'PhaseStatus__c', 'custom__phase_status')
    ),
    auditType: fmt(
      readCyberSweepClientTraversalString(r, data, 'auditType', suffix) ||
        gAll(r, data, 'auditTypeDisplayName', 'AuditTypeDisplayName__c', 'auditType', 'Audit_Type__c', 'AuditType__c', 'custom__audit_type')
    ),
    clientPersonalPhone: fmt(
      gAll(
        r,
        data,
        'clientPersonalPhone',
        'Client_Personal_Phone__c',
        `Client_Personal_Phone${suffix}`,
        'ClientPersonalPhone__c',
        'PersonPhone__c',
        `PersonPhone${suffix}`,
        'phone',
        'Phone',
        'Phone__c',
        'PersonPhone'
      )
    ),
    // Writes use PersonEmail + env suffix (see handleFieldSave); reads must match Gobodo/SF keys, not PersonalEmailAddress__c alone.
    personalEmailAddress: fmt(
      gAll(
        r,
        data,
        'personalEmailAddress',
        'PersonEmail',
        'PersonEmail__c',
        `PersonEmail${suffix}`,
        'PersonEmail__stg3_c',
        'PersonEmail__stg5_c',
        'personemail',
        'PersonalEmailAddress__c',
        'email',
        'Email__c',
        'custom__person_email'
      )
    ),
    profession: fmt(
      gAll(r, data, 'profession', 'Profession__c', `Profession${suffix}`, 'Profession__stg3_c', 'Profession__stg5_c', 'custom__profession')
    ),
    employer: fmt(
      gAll(r, data, 'employer', 'Employer__c', `Employer${suffix}`, 'Employer__stg3_c', 'Employer__stg5_c', 'custom__employer')
    ),
    // Subscription tab — Current Home Address (residential address)
    currentHomeAddress: fmt(
      gAll(
        r,
        data,
        'currentHomeAddress',
        'Current_Home_Address__c',
        `Current_Home_Address${suffix}`,
        'current_home_address__c',
        'custom__current_home_address',
        'PersonMailingStreet',
        'Address__c'
      )
    ),
    currentCity: fmt(
      gAll(
        r,
        data,
        'currentCity',
        'Current_City__c',
        `Current_City${suffix}`,
        'custom__current_city',
        'PersonMailingCity'
      )
    ),
    currentState: fmt(
      gAll(
        r,
        data,
        'currentState',
        'Current_State__c',
        `Current_State${suffix}`,
        'custom__current_state',
        'PersonMailingState'
      )
    ),
    currentZipPostal: fmt(
      gAll(
        r,
        data,
        'currentZipPostal',
        'Current_Zip_Postal__c',
        `Current_Zip_Postal${suffix}`,
        'custom__current_zip_postal',
        'PersonMailingPostalCode'
      )
    ),
    // Details tab — Billing Address (separate from current home address)
    billingStreet: fmt(
      gAll(r, data, 'billingStreet', 'BillingStreet', `BillingStreet${suffix}`, 'Billing_Street__c')
    ),
    billingCity: fmt(
      gAll(r, data, 'billingCity', 'BillingCity', `BillingCity${suffix}`, 'Billing_City__c')
    ),
    billingState: fmt(
      gAll(r, data, 'billingState', 'BillingState', `BillingState${suffix}`, 'Billing_State__c')
    ),
    billingPostalCode: fmt(
      gAll(r, data, 'billingPostalCode', 'BillingPostalCode', `BillingPostalCode${suffix}`, 'Billing_Postal_Code__c')
    ),
    billingCountry: fmt(
      gAll(r, data, 'billingCountry', 'BillingCountry', `BillingCountry${suffix}`, 'Billing_Country__c')
    ),
    previousHomeAddress: fmt(
      gAll(
        r,
        data,
        'previousHomeAddress',
        'Previous_Home_Address__c',
        `Previous_Home_Address${suffix}`,
        'custom__previous_home_address',
        'OtherStreet'
      )
    ),
    previousCity: fmt(
      gAll(
        r,
        data,
        'previousCity',
        'Previous_City__c',
        `Previous_City${suffix}`,
        'custom__previous_city',
        'OtherCity'
      )
    ),
    previousState: fmt(
      gAll(
        r,
        data,
        'previousState',
        'Previous_State__c',
        `Previous_State${suffix}`,
        'custom__previous_state',
        'OtherState'
      )
    ),
    previousZipPostal: fmt(
      gAll(
        r,
        data,
        'previousZipPostal',
        'Previous_Zip_Postal__c',
        `Previous_Zip_Postal${suffix}`,
        'custom__previous_zip_postal',
        'OtherPostalCode'
      )
    ),
    csReportsRecipients: fmt(
      gAll(
        r,
        data,
        'csReportsRecipients',
        'CS_Reports_Recipients__c',
        `CS_Reports_Recipients${suffix}`,
        'custom__cs_reports_recipients'
      )
    ),
    initialReport: asBool(
      gAll(r, data, 'initialReport', 'Initial_Report__c', `Initial_Report${suffix}`, 'custom__initial_report')
    ),
    initialReportDate: fmt(
      gAll(r, data, 'initialReportDate', 'Initial_Report_Date__c', `Initial_Report_Date${suffix}`, 'custom__initial_report_date')
    ),
    finalReport: asBool(gAll(r, data, 'finalReport', 'Final_Report__c', `Final_Report${suffix}`, 'custom__final_report')),
    finalReportDate: fmt(
      gAll(r, data, 'finalReportDate', 'Final_Report_Date__c', `Final_Report_Date${suffix}`, 'custom__final_report_date')
    ),
    quarterlySweep1: asBool(
      gAll(
        r,
        data,
        'quarterlySweep1',
        'Quarterly_Sweep_1__c',
        `Quarterly_Sweep_1${suffix}`,
        'quarterly_sweep_1__c',
        'custom__quarterly_sweep_1'
      )
    ),
    sweep1Date: fmt(gAll(r, data, 'sweep1Date', 'Sweep_1_Date__c', `Sweep_1_Date${suffix}`, 'custom__sweep_1_date')),
    quarterlySweep2: asBool(
      gAll(r, data, 'quarterlySweep2', 'Quarterly_Sweep_2__c', `Quarterly_Sweep_2${suffix}`, 'custom__quarterly_sweep_2')
    ),
    sweep2Date: fmt(gAll(r, data, 'sweep2Date', 'Sweep_2_Date__c', `Sweep_2_Date${suffix}`, 'custom__sweep_2_date')),
    quarterlySweep3: asBool(
      gAll(r, data, 'quarterlySweep3', 'Quarterly_Sweep_3__c', `Quarterly_Sweep_3${suffix}`, 'custom__quarterly_sweep_3')
    ),
    sweep3Date: fmt(gAll(r, data, 'sweep3Date', 'Sweep_3_Date__c', `Sweep_3_Date${suffix}`, 'custom__sweep_3_date')),
    quarterlySweep4: asBool(
      gAll(r, data, 'quarterlySweep4', 'Quarterly_Sweep_4__c', `Quarterly_Sweep_4${suffix}`, 'custom__quarterly_sweep_4')
    ),
    sweep4Date: fmt(gAll(r, data, 'sweep4Date', 'Sweep_4_Date__c', `Sweep_4_Date${suffix}`, 'custom__sweep_4_date')),
    otherProfession: fmt(
      gAll(r, data, 'otherProfession', 'Other_Profession__c', `Other_Profession${suffix}`, 'custom__other_profession')
    ),
    // Account Owner/Assigned Agent - store the ID, will be resolved to name later
    ownerId: fmt(gAll(r, data, 'OwnerId', 'Owner__c', 'ownerId', 'assignedAgent', 'Assigned_Agent__c', 'custom__owner')),
    ssnLast4: fmt(
      gAll(
        r,
        data,
        'ssnLast4',
        'Last_4_SSN__c',
        'SSN_Last_4__c',
        'SSNLast4__c',
        'last_4_ssn',
        'last4Ssn',
        'custom__last_4_ssn',
        'custom__last_four_ssn',
        `Last_4_SSN${suffix}`
      )
    ),
    ssn: fmt(
      gAll(
        r,
        data,
        'ssn',
        'SSN__c',
        'Social_Security_Number__c',
        'custom__ssn',
        `SSN${suffix}`,
        'SocialSecurityNumber__c'
      )
    ),
  };
};

const mergeRecordData = (
  client: Record<string, unknown> | null,
  audit: Record<string, unknown> | null,
  display: Record<string, unknown>
): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  // Merge audit FIRST, then client — so client fields (Name__c, etc.)
  // take priority over audit fields with the same key.
  if (audit) Object.assign(out, audit);
  if (client) Object.assign(out, client);
  Object.assign(out, display);
  return out;
};

const getStatusBadge = (status: string) => {
  const lower = status.toLowerCase();
  if (lower.includes('completed') || lower.includes('done')) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
  if (lower.includes('in progress') || lower.includes('active')) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  if (lower.includes('road') || lower.includes('block')) return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  if (lower.includes('pending') || lower.includes('not started')) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
  return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
};

// ─── SF-style Related List (empty-state-aware, reusable) ────────────────────
const SfRelatedList: React.FC<{
  title: string;
  count: number;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ title, count, icon, children }) => (
  <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-4">
    <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800">
      <div className="flex items-center gap-2">
        {icon || <ClipboardList className="h-5 w-5 text-blue-500" />}
        <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{title} ({count})</span>
      </div>
      <button className="px-3 py-1 text-xs font-medium border border-blue-500 text-blue-600 dark:text-blue-400 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20">
        New
      </button>
    </div>
    {children && <div className="border-t border-gray-200 dark:border-gray-700">{children}</div>}
  </div>
);

// ─── Inline Editable Field ─────────────────────────────────────────────────
const InlineEditField: React.FC<{
  label: string;
  value: string;
  fieldName: string;
  onSave: (fieldName: string, value: string) => Promise<void>;
  isEmail?: boolean;
  isPassword?: boolean;
}> = ({ label, value, fieldName, onSave, isEmail, isPassword }) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(value);
  const [isSaving, setIsSaving] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (editValue === value) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(fieldName, editValue);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save field:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') handleCancel();
  };

  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</p>
      {isEditing ? (
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type={isPassword ? 'text' : 'text'}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSaving}
            className="text-sm px-2 py-1 border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-100 flex-1"
          />
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="p-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 transition-colors disabled:opacity-50"
            title="Save"
          >
            <Save className="h-4 w-4" />
          </button>
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="p-2 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors disabled:opacity-50"
            title="Cancel"
          >
            <X className="h-4 w-4" />
          </button>
          {isSaving && <span className="text-xs text-gray-400">Saving...</span>}
        </div>
      ) : (
        <div className="flex items-center gap-2 group">
          {isEmail ? (
            <a href={`mailto:${value}`} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">{value || '—'}</a>
          ) : (
            <span className="text-sm text-gray-900 dark:text-gray-100">{value || '—'}</span>
          )}
          <Pencil
            className="h-3.5 w-3.5 text-gray-400 cursor-pointer hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={() => setIsEditing(true)}
          />
        </div>
      )}
    </div>
  );
};

// ─── Classic Audit Tab Content ─────────────────────────────────────────────
const ClassicAuditTabContent: React.FC<{
  audits: any[];
  isLoading: boolean;
  recordData?: Record<string, unknown>;
  onViewAudit?: (id: string) => void;
  onFieldSave?: (fieldName: string, value: unknown) => Promise<unknown>;
  onCreateAudit?: (auditData: any) => Promise<void>;
  clientId?: string;
  clientKey?: string; // Gobodo key for creating audits (e.g., "7c024ab8-d9fc-4353-879b-3c82cd96def6")
  clientName?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onOpenAuditsSettings?: () => void;
  templateIdToName?: Record<string, string>; // Map of template SF IDs to display names
}> = ({ audits, isLoading, recordData, onViewAudit, onFieldSave, onCreateAudit, clientId, clientKey, clientName, onRefresh, isRefreshing, onOpenAuditsSettings, templateIdToName = {} }) => {
  const [gmailOpen, setGmailOpen] = React.useState(true);
  const [isCreateAuditModalOpen, setIsCreateAuditModalOpen] = React.useState(false);

  // Sort audits by date (newest first)
  const sortedAudits = React.useMemo(() => {
    if (!audits || audits.length === 0) return [];
    return [...audits].sort((a, b) => {
      const aData = (a.data as Record<string, unknown>) ?? {};
      const bData = (b.data as Record<string, unknown>) ?? {};
      // Get due date from multiple possible fields
      const aDate = aData.Phase_1_Due_Date__c ?? aData.Due_Date__c ?? aData.dueDate ??
                   a.Phase_1_Due_Date__c ?? a.Due_Date__c ?? a.dueDate ??
                   aData.CreatedDate ?? a.CreatedDate ?? a.createdDate ?? '';
      const bDate = bData.Phase_1_Due_Date__c ?? bData.Due_Date__c ?? bData.dueDate ??
                   b.Phase_1_Due_Date__c ?? b.Due_Date__c ?? b.dueDate ??
                   bData.CreatedDate ?? b.CreatedDate ?? b.createdDate ?? '';
      // Parse dates and sort newest first
      const aTime = aDate ? new Date(String(aDate)).getTime() : 0;
      const bTime = bDate ? new Date(String(bDate)).getTime() : 0;
      return bTime - aTime; // Descending (newest first)
    });
  }, [audits]);

  const gmailEmail = (recordData?.gmailAccountEmail as string) || (recordData?.Dummy_Gmail_Email__c as string) || '';
  const gmailPassword = (recordData?.gmailAccountPassword as string) || (recordData?.Dummy_Gmail_Password__c as string) || '';
  const csUsername = (recordData?.csUsername as string) || (recordData?.CS_Username__c as string) || '';

  const handleFieldSave = async (fieldName: string, value: string) => {
    if (onFieldSave) {
      return await onFieldSave(fieldName, value);
    }
  };

  const handleCreateAudit = async (auditData: any, saveAndNew: boolean) => {
    if (onCreateAudit) {
      await onCreateAudit(auditData);
    }
    if (!saveAndNew) {
      setIsCreateAuditModalOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Dummy Gmail Account — collapsible SF-style section */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button
          onClick={() => setGmailOpen(!gmailOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-900/40 rounded-t-lg"
        >
          {gmailOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Dummy Gmail Account</span>
        </button>
        {gmailOpen && (
          <div className="px-4 py-4 grid grid-cols-2 gap-x-8 gap-y-3">
            <InlineEditField
              label="Email Address"
              value={gmailEmail}
              fieldName="gmailAccountEmail"
              onSave={handleFieldSave}
              isEmail
            />
            <InlineEditField
              label="Password"
              value={gmailPassword}
              fieldName="gmailAccountPassword"
              onSave={handleFieldSave}
            />
            <InlineEditField
              label="CS Username"
              value={csUsername}
              fieldName="csUsername"
              onSave={handleFieldSave}
            />
          </div>
        )}
      </div>

      {/* CS Audits related list */}
      {isLoading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4" />
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-blue-500" />
              <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">CS Audits ({audits.length})</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => onOpenAuditsSettings?.()}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                title="Settings"
              >
                <Settings className="h-4 w-4" />
              </button>
              {onRefresh && (
                <button
                  onClick={onRefresh}
                  disabled={isRefreshing}
                  className={`p-1.5 text-gray-400 hover:text-gray-600 rounded ${isRefreshing ? 'animate-spin' : ''}`}
                  title="Refresh"
                >
                  <RefreshCw className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => setIsCreateAuditModalOpen(true)}
                className="px-3 py-1 text-xs font-medium border border-blue-500 text-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                New
              </button>
            </div>
          </div>
          {sortedAudits.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900/50 border-y border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="w-8 px-2 py-2"><input type="checkbox" className="rounded" /></th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Audit Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Audit Type</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phase Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fiscal Year</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedAudits.map((audit: any, idx: number) => {
                      const id = audit.id ?? audit.key ?? audit.auditId ?? '';
                      const auditName = audit.name ?? audit.Name__c ?? audit.auditName ?? `Audit ${idx + 1}`;

                      // Audit Type/Template Name - use Audit_Type_Name__c which contains the human-readable name
                      // e.g., "Initial Deletion", "Initial Audit", "Final Audit", "Quarter #1 Audit"
                      // If that's not available, look up the template name from Audit_Template__c (SF ID)
                      const auditData = (audit.data as Record<string, unknown>) ?? {};
                      const directAuditType = audit.Audit_Type_Name__c || audit.auditTypeName ||
                                       auditData.Audit_Type_Name__c || auditData.auditTypeName || '';
                      // Get template SF ID for lookup
                      const templateSfId = audit.Audit_Template__c || audit.auditTemplate || audit.Audit_Type__c ||
                                          auditData.Audit_Template__c || auditData.auditTemplate || auditData.Audit_Type__c || '';
                      // Use direct name if available, otherwise lookup from template map
                      const auditType = directAuditType || templateIdToName[templateSfId] || templateSfId;

                      // Check multiple possible due date fields - check auditData FIRST since that's where Gobodo stores the fields
                      const dueDate = auditData.Phase_1_Due_Date__c ?? auditData.Due_Date__c ?? auditData.phase1DueDate ?? auditData.dueDate ??
                                     audit.Phase_1_Due_Date__c ?? audit.Due_Date__c ?? audit.phase1DueDate ?? audit.dueDate ??
                                     audit.custom__due_date ?? '';
                      const fiscalYear = audit.fiscalYear ?? audit.Fiscal_Year__c ?? audit.FiscalYear__c ?? audit.custom__fiscal_year ?? '';
                      const phase = audit.phaseStatus ?? audit.Phase_Status__c ?? audit.PhaseStatus__c ?? audit.custom__phase_status ?? '';
                      return (
                        <tr key={id || idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="w-8 px-2 py-2"><input type="checkbox" className="rounded" /></td>
                          <td className="px-3 py-2 text-sm text-blue-600 dark:text-blue-400 font-medium cursor-pointer hover:underline"
                              onClick={() => onViewAudit?.(String(id))}>
                            {auditName}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{auditType || '-'}</td>
                          <td className="px-3 py-2 text-sm">
                            {phase ? (
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(phase)}`}>
                                {phase}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{dueDate ? formatDate(dueDate) : '-'}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{fiscalYear || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-center py-2 border-t border-gray-200 dark:border-gray-700">
                <button className="text-sm text-blue-600 hover:underline">View All</button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-sm text-gray-500">No CS Audit records found.</div>
          )}
        </div>
      )}

      {/* Create CS Audit Modal */}
      <CreateCSAuditModal
        isOpen={isCreateAuditModalOpen}
        onClose={() => setIsCreateAuditModalOpen(false)}
        onSave={handleCreateAudit}
        clientName={clientName}
        clientId={clientKey || clientId}
      />
    </div>
  );
};

// ─── Automated Audit Tab Content ───────────────────────────────────────────
const AutomatedAuditTabContent: React.FC<{
  recordData?: Record<string, unknown>;
  onFieldSave?: (fieldName: string, value: unknown) => Promise<unknown>;
}> = ({ recordData, onFieldSave }) => {
  const [csipOpen, setCsipOpen] = React.useState(true);
  const pipCustomerId = (recordData?.pipCustomerId as string) || (recordData?.PIP_Customer_ID__c as string) || '';
  const scanFrequency = (recordData?.scanFrequencyOverride as string) || (recordData?.Scan_Frequency_Override__c as string) || 'Daily';
  const digestSchedule = (recordData?.digestScheduleOverride as string) || (recordData?.Digest_Schedule_Override__c as string) || 'Daily';

  const handleFieldSave = async (fieldName: string, value: string) => {
    if (onFieldSave) {
      return await onFieldSave(fieldName, value);
    }
  };

  return (
    <div className="space-y-6">
      {/* CSIP Information — collapsible SF-style section */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <button
          onClick={() => setCsipOpen(!csipOpen)}
          className="flex items-center gap-2 w-full px-4 py-3 text-left bg-gray-50 dark:bg-gray-900/40 rounded-t-lg"
        >
          {csipOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">CSIP Information</span>
        </button>
        {csipOpen && (
          <div className="px-4 py-4 grid grid-cols-2 gap-x-8 gap-y-3">
            <InlineEditField
              label="PIP Customer ID"
              value={pipCustomerId}
              fieldName="pipCustomerId"
              onSave={handleFieldSave}
            />
            <InlineEditField
              label="Scan Frequency Override"
              value={scanFrequency}
              fieldName="scanFrequencyOverride"
              onSave={handleFieldSave}
            />
            <InlineEditField
              label="Digest Schedule Override"
              value={digestSchedule}
              fieldName="digestScheduleOverride"
              onSave={handleFieldSave}
            />
          </div>
        )}
      </div>

      {/* CSIP Summaries */}
      <SfRelatedList title="CSIP Summaries" count={0} icon={<FileText className="h-5 w-5 text-red-500" />} />

      {/* CSIP Enrollments */}
      <SfRelatedList title="CSIP Enrollments" count={0} icon={<ClipboardList className="h-5 w-5 text-blue-500" />} />

      {/* CSIP Scans */}
      <SfRelatedList title="CSIP Scans" count={0} icon={<Scan className="h-5 w-5 text-orange-500" />} />

      {/* CSIP Cases */}
      <SfRelatedList title="CSIP Cases" count={0} icon={<Briefcase className="h-5 w-5 text-red-500" />} />
    </div>
  );
};

// ─── Configuration Tab Content (PII Monitoring) ───────────────────────────
const PII_CATEGORIES = [
  'Aliases', 'Email Addresses', 'Phone Numbers', 'Addresses',
  'Business License Number', 'Drivers License', 'Gamer Tag',
  'Medical ID', 'Mothers Maiden Name', 'Passport', 'Username',
  'PII Monitor Preferences',
] as const;

const ConfigurationTabContent: React.FC = () => (
  <div className="space-y-4">
    {PII_CATEGORIES.map((cat) => (
      <SfRelatedList
        key={cat}
        title={cat}
        count={0}
        icon={<Pencil className="h-5 w-5 text-blue-500" />}
      />
    ))}
  </div>
);

// Tabs after Configuration that should be hidden from the CyberSweep section
const HIDDEN_TAB_IDS = ['client_details_tab', 'billing_tab', 'activity_tab', 'documents_tab', 'notes_tab'];

const CyberSweepClientDetail: React.FC<CyberSweepClientDetailProps> = ({ clientId, auditId, onBack, onViewAudit }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  // Don't auto-select audit on mount - show client detail first, user can click to drill into audit
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const navigate = useNavigate();

  // NOTE: We intentionally don't auto-select the auditId prop anymore.
  // The client detail page should show first, and users click on an audit to view it.

  // Update URL when audit is selected inline (allows refresh to restore state)
  // Only navigate when selectedAuditId changes, not on clientId changes
  // The parent component handles navigation for clientId
  const prevSelectedAuditIdRef = React.useRef<string | null>(null);
  useEffect(() => {
    // Only update URL when selectedAuditId actually changes (user selects an audit)
    // This prevents re-navigation when browser back is pressed
    if (selectedAuditId !== prevSelectedAuditIdRef.current) {
      prevSelectedAuditIdRef.current = selectedAuditId;
      if (clientId) {
        if (selectedAuditId) {
          navigate(`/cybersweep/client/${clientId}/audit/${selectedAuditId}`, { replace: true });
        } else {
          // When no audit is selected, show just the client URL
          navigate(`/cybersweep/client/${clientId}`, { replace: true });
        }
      }
    }
  }, [selectedAuditId, clientId, navigate]);
  const [showAuditsSettingsModal, setShowAuditsSettingsModal] = useState(false);
  const [auditsSettings, setAuditsSettings] = useState<CSAuditsSettings>(defaultCSAuditsSettings);
  const { data: clientRecord } = useCyberSweepClient(USE_GOBODO_API ? clientId : null);
  const { data: auditRecord } = useCSAudit(USE_GOBODO_API ? auditId : null);
  const csAuditLookupClientId = useMemo(() => {
    if (!USE_GOBODO_API) return undefined;
    const rec = clientRecord as Record<string, unknown> | null;
    const candidate = String(rec?.key ?? rec?.id ?? clientId ?? '').trim();
    return candidate || undefined;
  }, [clientRecord, clientId]);
  const recordsForLookup = useMemo(() => {
    const arr: Record<string, unknown>[] = [];
    if (clientRecord) arr.push(clientRecord as Record<string, unknown>);
    if (auditRecord) arr.push(auditRecord as Record<string, unknown>);
    return arr;
  }, [clientRecord, auditRecord]);
  const { data: accountNameMap } = useAccountIdToName(recordsForLookup);
  const { data: contactNameMap } = useContactIdToName(recordsForLookup);
  const { data: sfUserNameMap } = useUserIdToName(recordsForLookup);
  const { data: users = [] } = useUsers({});
  const { data: csAudits, isLoading: csAuditsLoading, refetch: refetchCSAudits, isFetching: csAuditsFetching } = useCSAudits(csAuditLookupClientId);
  const { data: auditTemplates } = useAuditTemplates();
  const updateClient = useUpdateCyberSweepClient();
  const createCSAudit = useCreateCSAudit();

  // Build template ID to name map for audit type display
  const templateIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    if (auditTemplates) {
      for (const tpl of auditTemplates) {
        // GobodoAuditTemplate has: id, key, sfId, name
        const name = tpl.name || '';
        if (!name) continue;
        // Map by all possible ID fields for lookup
        if (tpl.id) map[tpl.id] = name;
        if (tpl.sfId) map[tpl.sfId] = name;
        if (tpl.key) map[tpl.key] = name;
      }
    }
    return map;
  }, [auditTemplates]);
  const { userName, userId } = useCurrentUser();

  // Build comprehensive user name map that includes all possible ID types
  const userNameMap = useMemo(() => {
    const map: Record<string, string> = { ...(sfUserNameMap || {}) };

    // Helper to add ID to map with lowercase variant
    const addToMap = (id: unknown, name: string) => {
      if (id && typeof id === 'string' && id.trim()) {
        map[id] = name;
        map[id.toLowerCase()] = name;
      }
    };

    // Also add lowercase versions of existing sfUserNameMap keys
    if (sfUserNameMap) {
      Object.entries(sfUserNameMap).forEach(([key, value]) => {
        if (key && typeof key === 'string') {
          map[key.toLowerCase()] = value;
        }
      });
    }

    users.forEach(user => {
      const userRec = user as Record<string, unknown>;
      const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
        || String(userRec.name || userRec.Name || '');
      if (!name) return;

      // Map all possible ID fields
      addToMap(user.id, name);
      addToMap(userRec.key, name);                    // Gobodo graph key
      addToMap(user.keycloakUserId, name);            // Keycloak UUID
      addToMap(user.graphObjectKey, name);            // Graph object key
      addToMap(userRec.sf_id, name);                  // Salesforce ID
      addToMap(userRec.sfId, name);                   // Salesforce ID (camelCase)
      addToMap(userRec.SfId, name);                   // Salesforce ID (PascalCase)
      addToMap(userRec.salesforce_id, name);          // Salesforce ID (snake_case)
      addToMap(userRec.SalesforceId, name);           // Salesforce ID (PascalCase)
      addToMap(userRec.userId, name);                 // Generic userId
      addToMap(userRec.user_id, name);                // Generic user_id
      addToMap(userRec.externalId, name);             // External ID
      addToMap(userRec.external_id, name);            // External ID (snake_case)
      addToMap(userRec.uuid, name);                   // UUID field
      addToMap(userRec.UUID, name);                   // UUID field (uppercase)
      addToMap(userRec.keycloak_user_id, name);       // Keycloak ID (snake_case)
      addToMap(userRec.KeycloakUserId, name);         // Keycloak ID (PascalCase)
    });

    return map;
  }, [sfUserNameMap, users]);

  const clientData = useMemo(() => {
    if (USE_GOBODO_API) {
      const fromClient = mapToClientData(clientRecord as Record<string, unknown> | null, clientId, auditId, accountNameMap);
      if (process.env.NODE_ENV === 'development') {
        console.log('[CyberSweepClientDetail] clientRecord:', clientRecord);
        console.log('[CyberSweepClientDetail] clientId prop:', clientId);
        console.log('[CyberSweepClientDetail] fromClient.id:', fromClient?.id);
      }
      if (fromClient?.firstName || fromClient?.lastName || fromClient?.clientName) return fromClient;
      return mapToClientData(auditRecord as Record<string, unknown> | null, clientId, auditId, accountNameMap);
    }
    let client = cyberSweepClientService.getClient(clientId);
    if (!client) client = cyberSweepClientService.getClientByAuditId(auditId);
    if (client) return client;
    return {
      id: clientId,
      auditId,
      firstName: 'John',
      lastName: 'Smith',
      clientName: 'John Smith',
      cyberSweepStartDate: '2025-01-15',
      cyberSweepEndDate: '2026-01-15',
      cyberSweepStage: 'Active Monitoring',
      cyberSweepStatus: 'In Progress',
      phaseStatus: '',
      auditType: '',
      clientPersonalPhone: '(555) 123-4567',
      personalEmailAddress: 'john.smith@email.com',
    };
  }, [clientRecord, auditRecord, clientId, auditId, accountNameMap]);

  const displayName = useMemo(() => {
    if (!clientData) return '';
    if (clientData.clientName) return clientData.clientName;
    return `${clientData.firstName} ${clientData.lastName}`.trim() || 'Unknown Client';
  }, [clientData]);

  const recordData = useMemo(() => {
    console.log('[recordData] clientData:', !!clientData, 'USE_GOBODO_API:', USE_GOBODO_API);
    if (!clientData) return undefined;
    if (USE_GOBODO_API) {
      const lookupDisplayFields = buildCyberSweepClientLookupDisplayFields(
        ((clientRecord as Record<string, unknown> | null) ?? (auditRecord as Record<string, unknown> | null)) || null,
        {
          accountNameMap,
          contactNameMap,
          userNameMap,
        }
      );
      // Extract system information fields from raw record
      const rec = clientRecord as Record<string, unknown> | null;
      const data = rec?.data as Record<string, unknown> ?? {};

      // Check for NAME fields first (saved by app), then fall back to ID fields
      // CreatedBy: check name field first, then ID
      const createdByName = fmt(gAll(rec ?? {}, data, 'CreatedBy__c', 'createdBy', 'CreatedByName__c', 'recordCreatedBy', 'created_by'));
      const createdById = fmt(gAll(rec ?? {}, data, 'CreatedById__c', 'CreatedById', 'createdById', 'created_by_id'));
      // LastModifiedBy: check name field first, then ID
      const lastModifiedByName = fmt(gAll(rec ?? {}, data, 'LastModifiedBy__c', 'lastModifiedBy', 'LastModifiedByName__c', 'recordModifiedBy', 'updatedBy', 'modified_by'));
      const lastModifiedById = fmt(gAll(rec ?? {}, data, 'LastModifiedById__c', 'LastModifiedById', 'lastModifiedById', 'updated_by_id', 'modified_by_id'));
      // Dates - also check updatedAt, modifiedAt, SystemModstamp
      const createdDate = fmt(gAll(rec ?? {}, data, 'CreatedDate__c', 'CreatedDate', 'createdDate', 'created_date', 'createdAt', 'created_at'));
      const lastModifiedDate = fmt(gAll(rec ?? {}, data, 'LastModifiedDate__c', 'LastModifiedDate', 'lastModifiedDate', 'lastModified', 'last_modified_date', 'updatedAt', 'updated_at', 'modifiedAt', 'SystemModstamp'));

      // Check if a value looks like an ID rather than a human name
      const looksLikeId = (value: string): boolean => {
        if (!value) return false;
        const v = value.trim();
        // Salesforce IDs: 15 or 18 alphanumeric chars, often starting with 005, 001, etc.
        if (/^[a-zA-Z0-9]{15}$|^[a-zA-Z0-9]{18}$/.test(v)) return true;
        // UUIDs: 8-4-4-4-12 hex pattern
        if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v)) return true;
        // Numeric IDs
        if (/^\d+$/.test(v)) return true;
        // Gobodo keys with suffix
        if (/__c$|__stg\d*_c$/i.test(v)) return true;
        return false;
      };

      // Try to resolve an ID to a name via userNameMap
      const lookupUserName = (id: string): string | null => {
        if (!id || !userNameMap) return null;
        const directMatch = userNameMap[id];
        if (directMatch) return directMatch;
        const lowerMatch = userNameMap[id.toLowerCase()];
        if (lowerMatch) return lowerMatch;
        const stripped = id.replace(/__c$|__stg5_c$/i, '');
        return userNameMap[stripped] || userNameMap[stripped.toLowerCase()] || null;
      };

      // Use name if available AND it's a real name (not an ID), otherwise try to resolve
      const resolveUserName = (name: string, id: string): string => {
        console.log('[resolveUserName] input - name:', name, 'id:', id);
        console.log('[resolveUserName] looksLikeId(name):', looksLikeId(name));

        // If name looks like an ID, try to look it up first
        if (name && looksLikeId(name)) {
          const resolved = lookupUserName(name);
          console.log('[resolveUserName] name is ID, lookup result:', resolved);
          if (resolved) return resolved;
        }
        // If name is a real name (not an ID), use it
        if (name && name.trim() && !looksLikeId(name)) {
          console.log('[resolveUserName] name is real name, returning:', name);
          return name;
        }
        // Try to resolve the ID
        const resolved = lookupUserName(id);
        console.log('[resolveUserName] id lookup result:', resolved);
        if (resolved) return resolved;
        // Fallback to whatever we have
        console.log('[resolveUserName] fallback to:', name || id || '');
        return name || id || '';
      };

      // DEBUG: Log Last Modified By resolution
      console.log('=== LAST MODIFIED BY DEBUG ===');
      console.log('lastModifiedByName:', lastModifiedByName);
      console.log('lastModifiedById:', lastModifiedById);
      console.log('userNameMap size:', Object.keys(userNameMap).length);
      console.log('userNameMap sample keys:', Object.keys(userNameMap).slice(0, 10));
      if (lastModifiedById) {
        console.log('Direct lookup:', userNameMap[lastModifiedById]);
        console.log('Lowercase lookup:', userNameMap[lastModifiedById.toLowerCase()]);
      }

      const resolvedCreatedBy = resolveUserName(createdByName, createdById);
      const resolvedLastModifiedBy = resolveUserName(lastModifiedByName, lastModifiedById);

      const suff = getCustomSuffix();
      const display: Record<string, unknown> = {
        clientName: displayName,
        firstName: clientData.firstName ?? '',
        lastName: clientData.lastName ?? '',
        email: clientData.personalEmailAddress,
        phone: clientData.clientPersonalPhone,
        socialSecurityNumber: clientData.ssn ?? '',
        ssn: clientData.ssn ?? '',
        ssnLast4: clientData.ssnLast4 ?? '',
        SSN__c: clientData.ssn ?? '',
        Last_4_SSN__c: clientData.ssnLast4 ?? '',
        [`SSN${suff}`]: clientData.ssn ?? '',
        [`Last_4_SSN${suff}`]: clientData.ssnLast4 ?? '',
        // DynamicFormSection resolves these camelCase field names via `getRecordFieldValue`, which doesn't
        // always hit the suffixed Gobodo keys (e.g. `Cyber_Sweep_Status__stg5_c`). Seed them explicitly so
        // picklists pick up the current value when entering edit mode instead of defaulting to empty.
        cyberSweepStartDate: clientData.cyberSweepStartDate ?? '',
        cyberSweepEndDate: clientData.cyberSweepEndDate ?? '',
        cyberSweepStage: clientData.cyberSweepStage ?? '',
        cyberSweepStatus: clientData.cyberSweepStatus ?? '',
        personalEmailAddress: clientData.personalEmailAddress ?? '',
        clientPersonalPhone: clientData.clientPersonalPhone ?? '',
        profession: clientData.profession ?? '',
        employer: clientData.employer ?? '',
        currentHomeAddress: clientData.currentHomeAddress ?? '',
        currentCity: clientData.currentCity ?? '',
        currentState: clientData.currentState ?? '',
        currentZipPostal: clientData.currentZipPostal ?? '',
        // Billing Address fields (separate from current home address - used by Details tab)
        // These come from lookupDisplayFields which extracts BillingStreet, BillingCity, etc.
        billingStreet: lookupDisplayFields.billingStreet ?? '',
        billingCity: lookupDisplayFields.billingCity ?? '',
        billingState: lookupDisplayFields.billingState ?? '',
        billingPostalCode: lookupDisplayFields.billingPostalCode ?? '',
        billingCountry: lookupDisplayFields.billingCountry ?? '',
        previousHomeAddress: clientData.previousHomeAddress ?? '',
        previousCity: clientData.previousCity ?? '',
        previousState: clientData.previousState ?? '',
        previousZipPostal: clientData.previousZipPostal ?? '',
        csReportsRecipients: clientData.csReportsRecipients ?? '',
        initialReport: clientData.initialReport,
        initialReportDate: clientData.initialReportDate ?? '',
        finalReport: clientData.finalReport,
        finalReportDate: clientData.finalReportDate ?? '',
        quarterlySweep1: clientData.quarterlySweep1,
        sweep1Date: clientData.sweep1Date ?? '',
        quarterlySweep2: clientData.quarterlySweep2,
        sweep2Date: clientData.sweep2Date ?? '',
        quarterlySweep3: clientData.quarterlySweep3,
        sweep3Date: clientData.sweep3Date ?? '',
        quarterlySweep4: clientData.quarterlySweep4,
        sweep4Date: clientData.sweep4Date ?? '',
        otherProfession: clientData.otherProfession ?? '',
        // Spread lookupDisplayFields first, then override with our resolved values
        ...lookupDisplayFields,
        // System Information fields - use resolved names (must come after spread to take precedence)
        createdBy: resolvedCreatedBy,
        createdDate: createdDate ? formatDateTime(createdDate) : '',
        lastModifiedBy: resolvedLastModifiedBy,
        lastModifiedDate: lastModifiedDate ? formatDateTime(lastModifiedDate) : '',
      };
      return mergeRecordData(
        clientRecord as Record<string, unknown> | null,
        auditRecord as Record<string, unknown> | null,
        display
      );
    }
    // Resolve ownerId to display name using userNameMap
    const ownerDisplayName = clientData.ownerId && userNameMap
      ? (userNameMap[clientData.ownerId] || clientData.ownerId)
      : (clientData.ownerId ?? '');
    return {
      clientName: displayName,
      accountOwner: ownerDisplayName,
      email: clientData.personalEmailAddress ?? '',
      phone: clientData.clientPersonalPhone ?? '',
      mobile: '',
      teamEmail: '',
      dateOfBirth: '',
      socialSecurityNumber: '',
      // Billing Address (separate from current home address)
      billingStreet: '',
      billingCity: '',
      billingState: '',
      billingPostalCode: '',
      billingCountry: '',
      recordCreatedBy: '',
      lastModifiedBy: '',
      clientNumber: clientData.id,
      status: clientData.cyberSweepStatus ?? 'active',
      dateJoined: clientData.cyberSweepStartDate ?? '',
      // Subscription fields
      cyberSweepStartDate: clientData.cyberSweepStartDate ?? '',
      cyberSweepEndDate: clientData.cyberSweepEndDate ?? '',
      cyberSweepStage: clientData.cyberSweepStage ?? '',
      cyberSweepStatus: clientData.cyberSweepStatus ?? '',
      clientPersonalPhone: clientData.clientPersonalPhone ?? '',
      personalEmailAddress: clientData.personalEmailAddress ?? '',
      currentHomeAddress: clientData.currentHomeAddress ?? '',
      currentCity: clientData.currentCity ?? '',
      currentState: clientData.currentState ?? '',
      currentZipPostal: clientData.currentZipPostal ?? '',
      previousHomeAddress: clientData.previousHomeAddress ?? '',
      previousCity: clientData.previousCity ?? '',
      previousState: clientData.previousState ?? '',
      previousZipPostal: clientData.previousZipPostal ?? '',
      csReportsRecipients: clientData.csReportsRecipients ?? '',
      initialReport: clientData.initialReport ? 'Yes' : '',
      initialReportDate: clientData.initialReportDate ?? '',
      finalReport: clientData.finalReport ? 'Yes' : '',
      finalReportDate: clientData.finalReportDate ?? '',
      quarterlySweep1: clientData.quarterlySweep1 ? 'Yes' : '',
      sweep1Date: clientData.sweep1Date ?? '',
      quarterlySweep2: clientData.quarterlySweep2 ? 'Yes' : '',
      sweep2Date: clientData.sweep2Date ?? '',
      quarterlySweep3: clientData.quarterlySweep3 ? 'Yes' : '',
      sweep3Date: clientData.sweep3Date ?? '',
      quarterlySweep4: clientData.quarterlySweep4 ? 'Yes' : '',
      sweep4Date: clientData.sweep4Date ?? '',
      profession: clientData.profession ?? '',
      otherProfession: clientData.otherProfession ?? '',
    };
  }, [clientData, clientRecord, auditRecord, displayName, accountNameMap, contactNameMap, userNameMap]);

  // Handler for creating a new CS Audit via Gobodo API
  const handleCreateAudit = async (auditData: Record<string, unknown>) => {
    // Combine date + time fields into ISO datetime strings (UTC format for consistency)
    const combineDateTime = (date: unknown, time: unknown): string | null => {
      const dateStr = String(date || '').trim();
      const timeStr = String(time || '').trim();
      if (!dateStr) return null;

      // If time is provided, combine date+time and convert to UTC ISO string
      if (timeStr) {
        const localDateTime = new Date(`${dateStr}T${timeStr}:00`);
        if (!isNaN(localDateTime.getTime())) {
          return localDateTime.toISOString();
        }
        return `${dateStr}T${timeStr}:00`;
      }
      // For date-only, store just the date (no time conversion) to avoid timezone confusion
      // This means "May 13" stays "May 13" regardless of timezone
      return dateStr;
    };

    // Use csAuditLookupClientId (the Gobodo key format) to ensure traversal API can find the audit
    const clientKeyForAudit = auditData.accountId || csAuditLookupClientId || clientData?.id;

    const now = new Date().toISOString();
    const mappedData: Record<string, unknown> = {
      // Link to the client/account - use the key format for traversal compatibility
      Account__c: clientKeyForAudit,
      custom__account: clientKeyForAudit,
      // Also store the client name directly for display (avoid lookup issues)
      ClientName__c: auditData.clientName || displayName,
      AccountName__c: auditData.clientName || displayName,

      // Information section
      Name__c: auditData.auditName,
      // Auditor - store both ID and name for display purposes
      Auditor__c: auditData.auditor,
      Auditor_Name__c: auditData.auditorName || auditData.auditor, // Name field for display
      auditorName: auditData.auditorName || auditData.auditor,
      Due_Date__c: auditData.dueDate,
      Fiscal_Year__c: auditData.fiscalYear,
      Audit_Type__c: auditData.auditType,
      Audit_Type_Name__c: auditData.auditTypeName,
      // Set template relationship for Gobodo traversals (links CSAudit to template)
      [`CSAuditTemplateRelField${getCustomSuffix()}`]: auditData.auditTypeGobodoKey || auditData.auditType,

      // Created By - store both ID and name for display
      CreatedById__c: userId,
      CreatedBy__c: userName,
      createdBy: userName,
      CreatedDate__c: now,
      createdDate: now,

      // Phase History section
      Phase_Status__c: auditData.phaseStatus,
      Road_Block_Note__c: auditData.roadBlockNote,
      Phase_1_Due_Date__c: auditData.phase1DueDate,
      Phase_2_Due_Date__c: auditData.phase2DueDate,
      Phase_1_Start_Date_Time__c: combineDateTime(auditData.phase1StartDate, auditData.phase1StartTime),
      Phase_2_Start_Date_Time__c: combineDateTime(auditData.phase2StartDate, auditData.phase2StartTime),
      Phase_1_Completed_Date_Time__c: combineDateTime(auditData.phase1CompletedDate, auditData.phase1CompletedTime),
      Phase_2_Completed_Date_Time__c: combineDateTime(auditData.phase2CompletedDate, auditData.phase2CompletedTime),
    };

    try {
      await createCSAudit.mutateAsync(mappedData);
      // Brief delay to allow Gobodo to establish graph relationships
      await new Promise(resolve => setTimeout(resolve, 500));
      // Force refetch the CS Audits list to show the new audit
      await refetchCSAudits();
    } catch (error) {
      console.error('[handleCreateAudit] Failed to create CS Audit:', error);
      throw error;
    }
  };

  // Callback for saving individual fields via DynamicFormSection
  const handleFieldSave = async (fieldName: string, value: unknown) => {
    if (!clientData?.id) return;

    const customSuffix = getCustomSuffix();

    // Map camelCase field names to Salesforce API field base names
    // These will be combined with the customSuffix to create the actual Gobodo field names
    const fieldBaseMap: Record<string, string> = {
      cyberSweepStatus: 'Cyber_Sweep_Status',
      cyberSweepStage: 'CyberSweep_Stage',
      cyberSweepStartDate: 'CyberSweep_Start_Date',
      cyberSweepEndDate: 'CyberSweep_End_Date',
      phaseStatus: 'Phase_Status',
      firstName: 'FirstName',
      lastName: 'LastName',
      clientPersonalPhone: 'Client_Personal_Phone',
      personalEmailAddress: 'PersonEmail',
      profession: 'Profession',
      employer: 'Employer',
      currentHomeAddress: 'Current_Home_Address',
      currentCity: 'Current_City',
      currentState: 'Current_State',
      currentZipPostal: 'Current_Zip_Postal',
      // Billing address fields (separate from current/home address)
      billingStreet: 'BillingStreet',
      billingCity: 'BillingCity',
      billingState: 'BillingState',
      billingPostalCode: 'BillingPostalCode',
      billingCountry: 'BillingCountry',
      previousHomeAddress: 'Previous_Home_Address',
      previousCity: 'Previous_City',
      previousState: 'Previous_State',
      previousZipPostal: 'Previous_Zip_Postal',
      csReportsRecipients: 'CS_Reports_Recipients',
      initialReport: 'Initial_Report',
      initialReportDate: 'Initial_Report_Date',
      finalReport: 'Final_Report',
      finalReportDate: 'Final_Report_Date',
      quarterlySweep1: 'Quarterly_Sweep_1',
      sweep1Date: 'Sweep_1_Date',
      quarterlySweep2: 'Quarterly_Sweep_2',
      sweep2Date: 'Sweep_2_Date',
      quarterlySweep3: 'Quarterly_Sweep_3',
      sweep3Date: 'Sweep_3_Date',
      quarterlySweep4: 'Quarterly_Sweep_4',
      sweep4Date: 'Sweep_4_Date',
      otherProfession: 'Other_Profession',
      // Dummy Gmail Account fields
      gmailAccountEmail: 'Dummy_Gmail_Email',
      gmailAccountPassword: 'Dummy_Gmail_Password',
      csUsername: 'CS_Username',
    };

    // Build the API field names - we need to update ALL variants that Gobodo might have
    // because the record may have data stored under different field name patterns
    const fieldBase = fieldBaseMap[fieldName];
    const updates: Record<string, unknown> = {};

    if (fieldBase) {
      // Write to ALL possible field name variants to ensure the update is picked up
      // regardless of which field the read path checks
      updates[`${fieldBase}${customSuffix}`] = value;           // e.g., Cyber_Sweep_Status__stg5_c
      updates[`${fieldBase}__c`] = value;                       // e.g., Cyber_Sweep_Status__c
      updates[`Custom__${fieldBase.toLowerCase()}__c`] = value; // e.g., Custom__cyber_sweep_status__c
      updates[`custom__${fieldBase.toLowerCase().replace(/_/g, '_')}`] = value; // e.g., custom__cyber_sweep_status
      // Also write to camelCase variant - this is critical because it's often checked FIRST in read paths
      // Convert Cyber_Sweep_Status → cyberSweepStatus by splitting on underscores and joining with proper casing
      const camelCase = fieldBase
        .split('_')
        .map((part, i) => i === 0 ? part.toLowerCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join('');
      updates[camelCase] = value;                               // e.g., cyberSweepStatus
    } else {
      updates[fieldName] = value;
    }

    // Always update LastModifiedBy and LastModifiedDate when saving any field
    const now = new Date().toISOString();
    updates['LastModifiedById__c'] = userId;
    updates['LastModifiedBy__c'] = userName;      // SF-style name field
    updates['lastModifiedBy'] = userName;         // camelCase name field
    updates['LastModifiedDate__c'] = now;
    updates['lastModifiedDate'] = now;
    updates['lastModified'] = now;

    try {
      return await updateClient.mutateAsync({
        id: clientData.id,
        updates,
        detailClientId: clientId,
      });
    } catch (error) {
      console.error('[handleFieldSave] Failed to save field:', error);
      throw error;
    }
  };

  if (USE_GOBODO_API && !clientData) {
    return (
      <GobodoRequiredGuard>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="animate-pulse text-gray-500 dark:text-gray-400">Loading client...</div>
        </div>
      </GobodoRequiredGuard>
    );
  }

  const status = clientData?.phaseStatus || clientData?.cyberSweepStatus || clientData?.cyberSweepStage || '';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <StandardButton
            variant="ghost"
            leftIcon={<ArrowLeft className="h-5 w-5" />}
            onClick={onBack}
            className="mb-4"
          >
            Back to Operations Dashboard
          </StandardButton>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                {displayName}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <p className="text-gray-600 dark:text-gray-400">
                  Client ID: {clientData?.id}
                </p>
                {status && (
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(status)}`}>
                    {status}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StandardButton
                variant="secondary"
                leftIcon={<Mail className="h-4 w-4" />}
                onClick={() => setShowEmailModal(true)}
              >
                Send Email
              </StandardButton>
              {onViewAudit && (auditId || clientData?.auditId) && (
                <StandardButton
                  variant="secondary"
                  leftIcon={<FileText className="h-4 w-4" />}
                  onClick={() => onViewAudit(auditId || clientData?.auditId || '')}
                >
                  View Audit
                </StandardButton>
              )}
            </div>
          </div>
        </div>

        <SendEmailModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          recordId={clientId}
          recordType="client"
          recipientEmail={clientData?.personalEmailAddress ?? ''}
          recipientName={displayName ?? ''}
          onEmailSent={() => {}}
          department="cybersweep"
          emailContext={{
            client: clientData ? {
              id: clientData.id,
              name: displayName ?? '',
              email: clientData.personalEmailAddress ?? '',
              phone: clientData.clientPersonalPhone ?? '',
              segment: '',
              status: clientData.cyberSweepStatus ?? '',
              owner: '',
              lastContact: '',
              riskTier: '',
              accountName: '',
              accountOwner: '',
              accountManager: '',
            } : undefined,
            system: {
              date: new Date().toLocaleDateString(),
              time: new Date().toLocaleTimeString(),
              url: typeof window !== 'undefined' ? window.location.origin : '',
              companyName: 'Regal Credit Management',
            },
          }}
        />

        {/* CS Audits Settings Modal */}
        <CSAuditsSettingsModal
          isOpen={showAuditsSettingsModal}
          onClose={() => setShowAuditsSettingsModal(false)}
          currentSettings={auditsSettings}
          onApply={(newSettings) => {
            setAuditsSettings(newSettings);
            console.log('[CSAuditsSettings] Applied settings:', newSettings);
          }}
        />

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Contact Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center mb-3">
              <User className="h-5 w-5 text-blue-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Contact</h3>
            </div>
            <div className="space-y-2">
              {clientData?.personalEmailAddress && (
                <div className="flex items-center text-sm">
                  <Mail className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-gray-600 dark:text-gray-400 truncate">{clientData.personalEmailAddress}</span>
                </div>
              )}
              {clientData?.clientPersonalPhone && (
                <div className="flex items-center text-sm">
                  <Phone className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                  <span className="text-gray-600 dark:text-gray-400">{clientData.clientPersonalPhone}</span>
                </div>
              )}
              {!clientData?.personalEmailAddress && !clientData?.clientPersonalPhone && (
                <p className="text-sm text-gray-400">No contact info available</p>
              )}
            </div>
          </div>

          {/* Audit Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center mb-3">
              <Shield className="h-5 w-5 text-purple-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Audit</h3>
            </div>
            <div className="space-y-2">
              {/* Use prop auditId, fallback to clientData.auditId, then latest from csAudits */}
              {(() => {
                // Get latest audit from csAudits query results as fallback
                const latestCsAudit = csAudits && csAudits.length > 0 ? csAudits[0] : null;
                const latestCsAuditId = latestCsAudit ? String(latestCsAudit.id ?? latestCsAudit.key ?? '') : '';
                const displayAuditId = auditId || clientData?.auditId || latestCsAuditId;
                // Get human-readable audit name (auto-number like "CyberAudit-00001") from latest audit
                const auditName = latestCsAudit
                  ? String(latestCsAudit.name ?? latestCsAudit.Name__c ?? latestCsAudit.label ?? '')
                  : '';
                // Display the audit name if available, otherwise fall back to truncated ID
                const displayText = auditName || (displayAuditId ? `${displayAuditId.slice(0, 12)}${displayAuditId.length > 12 ? '...' : ''}` : '');
                return (
                  <div className="text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Audit ID: </span>
                    {displayAuditId ? (
                      <button
                        onClick={() => onViewAudit?.(displayAuditId)}
                        className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                      >
                        {displayText}
                      </button>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 italic">No audit</span>
                    )}
                  </div>
                );
              })()}
              {clientData?.auditType && (
                <div className="text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Type: </span>
                  <span className="text-gray-900 dark:text-gray-100">{clientData.auditType}</span>
                </div>
              )}
              {clientData?.cyberSweepStage && (
                <div className="text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Stage: </span>
                  <span className="text-gray-900 dark:text-gray-100">{clientData.cyberSweepStage}</span>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center mb-3">
              <Calendar className="h-5 w-5 text-green-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Dates</h3>
            </div>
            <div className="space-y-2">
              <div className="text-sm">
                <span className="text-gray-500 dark:text-gray-400">Start: </span>
                <span className="text-gray-900 dark:text-gray-100">
                  {clientData?.cyberSweepStartDate ? formatDate(clientData.cyberSweepStartDate) : '—'}
                </span>
              </div>
              {/* Show next audit due date from latest CS Audit */}
              {(() => {
                // Get the next due date from audits - find the earliest upcoming due date
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                let nextDueDate: string | null = null;
                if (csAudits && csAudits.length > 0) {
                  const upcomingDueDates = csAudits
                    .map((audit: any) => {
                      const data = (audit.data as Record<string, unknown>) ?? {};
                      return data.Phase_1_Due_Date__c ?? data.Due_Date__c ?? data.phase1DueDate ??
                             audit.Phase_1_Due_Date__c ?? audit.Due_Date__c ?? audit.phase1DueDate ?? null;
                    })
                    .filter((d): d is string => d != null && String(d).trim() !== '')
                    .map(d => ({ date: new Date(String(d)), raw: String(d) }))
                    .filter(d => !isNaN(d.date.getTime()) && d.date >= today)
                    .sort((a, b) => a.date.getTime() - b.date.getTime());
                  if (upcomingDueDates.length > 0) {
                    nextDueDate = upcomingDueDates[0].raw;
                  }
                }
                return (
                  <div className="text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Next Due: </span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {nextDueDate ? formatDate(nextDueDate) : '—'}
                    </span>
                  </div>
                );
              })()}
              <div className="text-sm">
                <span className="text-gray-500 dark:text-gray-400">End: </span>
                <span className="text-gray-900 dark:text-gray-100">
                  {clientData?.cyberSweepEndDate ? formatDate(clientData.cyberSweepEndDate) : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center mb-3">
              <FileText className="h-5 w-5 text-orange-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Status</h3>
            </div>
            <div className="space-y-2">
              {clientData?.phaseStatus && (
                <div className="text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Phase: </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(clientData.phaseStatus)}`}>
                    {clientData.phaseStatus}
                  </span>
                </div>
              )}
              {clientData?.cyberSweepStatus && (
                <div className="text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Status: </span>
                  <span className="text-gray-900 dark:text-gray-100">{clientData.cyberSweepStatus}</span>
                </div>
              )}
              {!clientData?.phaseStatus && !clientData?.cyberSweepStatus && (
                <p className="text-sm text-gray-400">No status info</p>
              )}
            </div>
          </div>
        </div>

        {/* CS Audit drill-in detail view */}
        {selectedAuditId && (
          <CyberSweepAuditDetail
            auditId={selectedAuditId}
            onBack={() => setSelectedAuditId(null)}
          />
        )}

        {/* Dynamic Tab Renderer (hidden when viewing audit detail) */}
        {clientData && !selectedAuditId && (
          <DynamicTabRenderer
            applicationName="cybersweep"
            pageId="cybersweep_client_detail"
            recordId={clientData.id}
            recordData={recordData}
            recordType="client"
            userRole="admin"
            onViewAuditDetails={(id) => setSelectedAuditId(id)}
            contactName={displayName}
            phoneNumber={clientData.clientPersonalPhone}
            relatedToName={displayName}
            hiddenTabIds={HIDDEN_TAB_IDS}
            onFieldSave={handleFieldSave}
            customTabContent={{
              classic_audit_tab: () => (
                <ClassicAuditTabContent
                  audits={csAudits ?? []}
                  isLoading={csAuditsLoading}
                  recordData={recordData}
                  onViewAudit={(id) => {
                    // Set local state to show audit inline
                    setSelectedAuditId(id);
                  }}
                  onFieldSave={handleFieldSave}
                  onCreateAudit={handleCreateAudit}
                  clientId={clientData?.id}
                  clientKey={csAuditLookupClientId}
                  clientName={displayName}
                  onRefresh={() => refetchCSAudits()}
                  isRefreshing={csAuditsFetching}
                  onOpenAuditsSettings={() => setShowAuditsSettingsModal(true)}
                  templateIdToName={templateIdToName}
                />
              ),
              automated_audit_tab: () => (
                <AutomatedAuditTabContent recordData={recordData} onFieldSave={handleFieldSave} />
              ),
              configuration_tab: () => (
                <ConfigurationTabContent />
              ),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default CyberSweepClientDetail;
